import { useNavigate } from 'react-router-dom';
import IconSvg from 'src/assets/icons/IconSvg';
import FavoriteRaffleCard from '../components/FavoriteRaffleCard';
import { useViewport } from 'src/context/ViewportContext';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { useCountdown } from 'src/hooks/useCountdown';
import type { Raffle, RaffleFavorite } from 'src/types/raffle';

const satsToBtc = (sats: number) => (sats / 1e8).toFixed(4);

interface Props {
  activeView: 'list' | 'grid';
  favorites: RaffleFavorite[];
  loading: boolean;
  userId: string;
  onToggleFavorite: (raffleId: string) => void;
}

const FavoriteRaffleTab = ({ activeView, favorites, loading, userId, onToggleFavorite }: Props) => {
  if (loading) {
    return <div className="py-10 text-center text-grayscale-400">Loading favorites...</div>;
  }

  if (favorites.length === 0) {
    return <div className="py-10 text-center text-grayscale-400">No raffles found</div>;
  }

  if (activeView === 'list') {
    return (
      <FavoriteRaffleTable
        favorites={favorites}
        userId={userId}
        onToggleFavorite={onToggleFavorite}
      />
    );
  }

  return (
    <div className="grid gap-[18px] grid-cols-[repeat(auto-fill,minmax(320px,1fr))]">
      {favorites.map((fav) => (
        <FavoriteRaffleCard
          key={fav.id}
          raffle={fav.raffle}
          userId={userId}
          onToggleFavorite={onToggleFavorite}
        />
      ))}
    </div>
  );
};

export default FavoriteRaffleTab;

interface TableProps {
  favorites: RaffleFavorite[];
  userId: string;
  onToggleFavorite: (raffleId: string) => void;
}

const FavoriteRaffleTable = ({ favorites, userId, onToggleFavorite }: TableProps) => {
  const { isMobile } = useViewport();

  return (
    <>
      {!isMobile ? (
        <div className="py-3 rounded-lg bg-grayscale-900">
          <table className="w-full">
            <thead className="text-sm text-left text-grayscale-300">
              <tr>
                <th className="w-[348px] py-[13px] pl-[58px]">Raffle Name</th>

                <th className="w-[188px] py-[13px]">
                  <div className="flex gap-1 items-center">
                    <span>Ticket Price</span>
                    <IconSvg icon="trianglesS" />
                  </div>
                </th>

                <th className="w-[188px] py-[13px]">
                  <div className="flex gap-1 items-center">
                    <span>Sold / Total Tickets</span>
                    <IconSvg icon="trianglesS" />
                  </div>
                </th>

                <th className="w-[188px] py-[13px]">Total Tickets Value</th>

                <th className="w-[188px] py-[13px]">
                  <div className="flex gap-1 items-center">
                    <span>Time Remaining</span>
                    <IconSvg icon="trianglesS" />
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr style={{ height: '10px' }}></tr>

              {favorites.map((fav) => (
                <FavoriteRaffleTableRow
                  key={fav.id}
                  raffle={fav.raffle}
                  userId={userId}
                  onToggleFavorite={onToggleFavorite}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid gap-2.5">
          {favorites.map((fav) => (
            <FavoriteRaffleMobileRow
              key={fav.id}
              raffle={fav.raffle}
              onToggleFavorite={onToggleFavorite}
            />
          ))}
        </div>
      )}
    </>
  );
};

const FavoriteRaffleTableRow = ({
  raffle,
  userId,
  onToggleFavorite,
}: {
  raffle: Raffle;
  userId: string;
  onToggleFavorite: (id: string) => void;
}) => {
  const navigate = useNavigate();
  const isEnded = raffle.status === 'ENDED' || raffle.status === 'COMPLETED';
  const countdown = useCountdown(raffle.endDate);
  const endDate = new Date(raffle.endDate);
  const formattedEndDate = `${endDate.getDate()} ${endDate.toLocaleString('en', { month: 'long' })} ${endDate.getFullYear()}`;
  const imageUrl = raffle.ordinalImageUrl || raffle.runesImageUrl;

  return (
    <tr
      className="text-lg rounded-lg transition-all duration-200 cursor-pointer text-grayscale-300 hover:bg-grayscale-700"
      onClick={() => navigate(`/raffle/${raffle.id}`)}
    >
      <td className="py-3.5 pl-5 rounded-l-lg">
        <div className="flex gap-3 items-center">
          <button onClick={e => { e.stopPropagation(); onToggleFavorite(raffle.id); }}>
            <IconSvg icon="starM" className="text-brand-tangerine" />
          </button>

          <div className="flex gap-3 items-center">
            {imageUrl ? (
              <img src={imageUrl} alt="" className="flex-shrink-0 w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="flex-shrink-0 w-12 h-12 bg-grayscale-600 rounded-full flex items-center justify-center text-xs text-grayscale-300">
                {raffle.prizeType === 'RUNES' ? 'R' : '?'}
              </div>
            )}

            <div className="space-y-1">
              <div className="flex gap-1 items-center">
                <div className="text-lg font-semibold text-brand-magnolia">
                  {raffle.name ?? 'Untitled'}
                </div>
                <IconSvg icon="verifiedS" className="text-brand-tangerine" />
              </div>
              <span className="text-xss text-grayscale-300">
                Created by: {raffle.creator?.id === userId ? 'You' : 'Creator'}
              </span>
            </div>
          </div>
        </div>
      </td>

      <td>
        <div>
          <div className="flex gap-1 items-center text-grayscale-100">
            <IconSvg icon="bitcoinS" />
            <div className="text-lg font-semibold text-grayscale-100">
              {satsToBtc(raffle.ticketPriceSats)}
            </div>
          </div>
        </div>
      </td>

      <td>{raffle.ticketsSold} / {raffle.supply.toLocaleString()}</td>

      <td>
        <div>
          <div className="flex gap-1 items-center text-grayscale-100">
            <IconSvg icon="bitcoinS" />
            <div className="text-lg font-semibold text-grayscale-100">
              {satsToBtc(raffle.totalTicketValue)}
            </div>
          </div>
        </div>
      </td>

      <td className="pr-5 rounded-r-lg">
        {isEnded ? (
          <div>
            <div className="text-lg text-system-error">Raffle Ended</div>
            <div className="text-xs text-grayscale-400">{formattedEndDate}</div>
          </div>
        ) : (
          <div className="text-lg font-medium text-grayscale-100">{countdown}</div>
        )}
      </td>
    </tr>
  );
};

const FavoriteRaffleMobileRow = ({
  raffle,
  onToggleFavorite,
}: {
  raffle: Raffle;
  onToggleFavorite: (id: string) => void;
}) => {
  const navigate = useNavigate();
  const isLive = raffle.status === 'LIVE';
  const countdown = useCountdown(raffle.endDate);
  const imageUrl = raffle.ordinalImageUrl || raffle.runesImageUrl;

  return (
    <Disclosure
      as="div"
      className="overflow-hidden relative p-4 rounded-lg bg-grayscale-800 cursor-pointer"
      onClick={() => navigate(`/raffle/${raffle.id}`)}
    >
      <div className="absolute top-0 left-0 w-[38px] h-[41px] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="w-0 h-0 border-t-[41px] border-t-grayscale-700 border-r-[38px] border-r-transparent">
          <button onClick={() => onToggleFavorite(raffle.id)}>
            <IconSvg
              icon="starM"
              className="absolute top-[3px] left-[3px] text-brand-tangerine w-[18px] h-[18px]"
            />
          </button>
        </div>
      </div>

      <DisclosureButton className="flex relative justify-between items-center w-full rounded-lg group" onClick={e => e.stopPropagation()}>
        <div className="flex gap-3 items-center">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="flex-shrink-0 w-12 h-12 rounded-full object-cover" />
          ) : (
            <div className="flex-shrink-0 w-12 h-12 bg-grayscale-600 rounded-full flex items-center justify-center text-xs text-grayscale-300">
              {raffle.prizeType === 'RUNES' ? 'R' : '?'}
            </div>
          )}

          <div className="space-y-1">
            <div className="flex gap-1 items-center">
              <div className="text-lg font-semibold text-brand-magnolia">
                {raffle.name ?? 'Untitled'}
              </div>
              <IconSvg icon="verifiedS" className="text-brand-tangerine" />
            </div>

            {isLive && (
              <div className="flex gap-1 items-center text-system-success">
                <IconSvg icon="liveS" />
                <span className="leading-none text-xss">{countdown}</span>
              </div>
            )}
          </div>
        </div>

        <IconSvg
          icon="arrowDownM"
          className="text-grayscale-300 group-data-[open]:rotate-180 transition-all duration-200"
        />
      </DisclosureButton>

      <DisclosurePanel className="!mt-3 transition-all duration-200">
        <div className="flex justify-between items-start py-3 border-t border-grayscale-700">
          <div>
            <div className="text-xs text-grayscale-300">Tickets Price</div>
            <div className="flex gap-1 items-center text-grayscale-100">
              <IconSvg icon="bitcoinS" />
              <div className="font-semibold text-grayscale-100">{satsToBtc(raffle.ticketPriceSats)}</div>
            </div>
          </div>

          <div className="text-right">
            <div className="text-xs text-grayscale-300">Sold / Total Tickets</div>
            <div className="font-semibold text-grayscale-100">
              {raffle.ticketsSold} / {raffle.supply.toLocaleString()}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-start py-3 border-t border-grayscale-700">
          <div>
            <div className="text-xs text-grayscale-300">Total Tickets Value</div>
            <div className="flex gap-1 items-center text-grayscale-100">
              <IconSvg icon="bitcoinS" />
              <div className="font-semibold text-grayscale-100">{satsToBtc(raffle.totalTicketValue)}</div>
            </div>
          </div>

          <div className="text-right">
            <div className="text-xs text-grayscale-300">Created by:</div>
            <div className="font-semibold text-grayscale-100">Creator</div>
          </div>
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
};
